// Where a line is known to be closed, from its alerts (GET /alerts), so a
// train placed by the timetable alone (GtfsTrip.scheduled) isn't drawn
// running through it. computeTrains applies it; the city page (page.ts) and
// the world map (apps/world gtfs-region.ts) pass it the alerts they already
// fetch, so every city gets the same rule:
//
// - a line whose alert closes it (kind 'closed'): none of its scheduled trains;
// - a part closure (kind 'part') whose section is named reliably, "between A
//   and B" with A and B two of the line's stations: none on the track between
//   them; a part closure whose section can't be named that way changes nothing;
// - live trains are never hidden, and nothing is made up or thinned from an
//   alert's words ("every 8 minutes").

import type { Network, NetworkLine, StationId } from '@tol/core/types';
import type { AlertItem, AlertKind, LineAlert } from './types.ts';

export interface Closures {
  // Lines closed whole.
  readonly lines: ReadonlySet<string>;
  // Line -> its closed track, as adjacent station pairs (pairKey).
  readonly track: ReadonlyMap<string, ReadonlySet<string>>;
}

export const NO_CLOSURES: Closures = { lines: new Set(), track: new Map() };

// A pair of stations either way round.
const pairKey = (a: StationId, b: StationId): string => (a < b ? `${a}|${b}` : `${b}|${a}`);

// What an alert does to its line, for a city whose server doesn't say
// (LineAlert.kind): from its type ("Part Suspended", "Delays").
export function alertKind(a: LineAlert | AlertItem): AlertKind {
  if (a.kind !== undefined) return a.kind;
  const t = a.type.toLowerCase();
  // A stop's closure or move ("Stop closed", rt-alerts.ts) is the stop's.
  if (/^stops? /u.test(t)) return 'changed';
  if (/part(ly)? (suspend|clos)|substitute bus/u.test(t)) return 'part';
  if (/suspend|clos|no service|not running/u.test(t)) return 'closed';
  if (/delay|slow|reduced|cancel/u.test(t)) return 'delays';
  return 'changed';
}

// Words as the alerts and the stations' names both spell them, near enough:
// lowercase, no accents or punctuation, "Street" and "St" alike.
const SAME: Readonly<Record<string, string>> = { street: 'st', avenue: 'av', ave: 'av', road: 'rd' };
function words(s: string): string[] {
  return s
    .normalize('NFD')
    .replaceAll(/\p{M}/gu, '')
    .toLowerCase()
    .replaceAll('&', ' and ')
    .split(/[^\p{L}\p{N}]+/u)
    .filter((w) => w !== '')
    .map((w) => SAME[w] ?? w);
}

// Each of the line's stations by the words of its name, and of each part of
// a name with a slash ("Cooper Street/Rutgers": "Cooper Street" too). A name
// two of its stations share names neither.
function namesOf(net: Network, L: NetworkLine): ReadonlyMap<string, StationId> {
  const out = new Map<string, StationId | null>();
  for (const id of L.ids) {
    const name = net.stations.get(id)?.name ?? '';
    const parts = name.includes('/') ? [name, ...name.split('/')] : [name];
    for (const part of parts) {
      const key = words(part).join(' ');
      if (key === '') continue;
      const had = out.get(key);
      out.set(key, had === undefined || had === id ? id : null);
    }
  }
  const unique = new Map<string, StationId>();
  for (const [k, id] of out) if (id !== null) unique.set(k, id);
  return unique;
}

// The station whose name starts at word i, the longest that fits, and the
// word after it (and after a "station" or "stations" following it).
function stationAt(
  text: readonly string[],
  i: number,
  names: ReadonlyMap<string, StationId>,
): { readonly id: StationId; readonly end: number } | null {
  for (let end = Math.min(text.length, i + 8); end > i; end--) {
    const id = names.get(text.slice(i, end).join(' '));
    if (id === undefined) continue;
    const after = text[end];
    return { id, end: after === 'station' || after === 'stations' ? end + 1 : end };
  }
  return null;
}

// The track an alert's words close on a line: for each "between A and B"
// naming two of its stations, the hops between them on every pattern that
// has both. Null when it names none that way: the section isn't known.
export function closedSection(net: Network, lineId: string, text: string): ReadonlySet<string> | null {
  const L = net.lines[lineId];
  if (!L) return null;
  const names = namesOf(net, L);
  const w = words(text);
  const out = new Set<string>();
  let named = false;
  for (let k = 0; k < w.length; k++) {
    if (w[k] !== 'between') continue;
    const a = stationAt(w, k + 1, names);
    if (!a || w[a.end] !== 'and') continue;
    const b = stationAt(w, a.end + 1, names);
    if (!b || b.id === a.id) continue;
    for (const { ids } of L.seqs) {
      const i = ids.indexOf(a.id);
      const j = ids.indexOf(b.id);
      if (i < 0 || j < 0) continue;
      named = true;
      for (let x = Math.min(i, j); x < Math.max(i, j); x++) {
        const from = ids[x];
        const to = ids[x + 1];
        if (from !== undefined && to !== undefined) out.add(pairKey(from, to));
      }
    }
  }
  return named ? out : null;
}

// Each line's closures, from its alerts (in the network's line ids).
export function closuresOf(net: Network, alerts: Readonly<Record<string, LineAlert>>): Closures {
  const lines = new Set<string>();
  const track = new Map<string, ReadonlySet<string>>();
  for (const [lineId, alert] of Object.entries(alerts)) {
    if (alertKind(alert) === 'closed') {
      lines.add(lineId);
      continue;
    }
    const closed = new Set<string>();
    for (const item of alert.all ?? [alert]) {
      if (alertKind(item) !== 'part') continue;
      const section = closedSection(net, lineId, `${item.text} ${item.detail ?? ''}`);
      for (const pair of section ?? []) closed.add(pair);
    }
    if (closed.size > 0) track.set(lineId, closed);
  }
  return { lines, track };
}

// Whether a train on the hop from `from` to `to` runs over closed track: the
// hop's stations on the pattern that stops at the most of them between the
// two (an express hop runs over the local stations it skips). With no
// `from` (waiting at `to`), whether every track out of `to` is closed.
export function hopClosed(
  net: Network,
  closures: Closures,
  lineId: string,
  from: StationId | null,
  to: StationId,
): boolean {
  const closed = closures.track.get(lineId);
  const L = net.lines[lineId];
  if (!closed || !L) return false;
  if (from === null || from === '') {
    const next = L.adj.get(to);
    return next !== undefined && next.size > 0 && [...next].every((n) => closed.has(pairKey(to, n)));
  }
  let best: readonly StationId[] = [from, to];
  for (const { ids } of L.seqs) {
    const i = ids.indexOf(from);
    const j = ids.indexOf(to);
    if (i < 0 || j < 0) continue;
    const run = ids.slice(Math.min(i, j), Math.max(i, j) + 1);
    if (run.length > best.length) best = run;
  }
  for (let x = 0; x + 1 < best.length; x++) {
    const a = best[x];
    const b = best[x + 1];
    if (a !== undefined && b !== undefined && closed.has(pairKey(a, b))) return true;
  }
  return false;
}
